import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Gift, Copy, Mail, Share2, Wallet, Users, Sparkles } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

export default function Parrainage() {
  const { t } = useTranslation();
  const { user, loading: authLoading } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!user) return;
    setLoading(true);
    const token = localStorage.getItem("token");
    axios
      .get(`${API}/api/referral/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [user]);

  const shareUrl = data
    ? `${typeof window !== "undefined" ? window.location.origin : "https://nordstellatech.fr"}/register?ref=${data.referral_code}`
    : "";

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success(t("referral.link_copied"));
    } catch {
      toast.error(t("referral.copy_error"));
    }
  };

  const shareByEmail = () => {
    const subject = encodeURIComponent(t("referral.email_subject"));
    const body = encodeURIComponent(t("referral.email_body", { url: shareUrl }));
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const shareWhatsapp = () => {
    const text = encodeURIComponent(t("referral.share_text", { url: shareUrl }));
    window.open(`https://wa.me/?text=${text}`, "_blank", "noopener");
  };

  return (
    <div data-testid="parrainage-page" className="bg-[#001F3F] text-white">
      {/* ─── HERO PITCH ─── */}
      <section className="border-b border-white/10">
        <div className="max-w-5xl mx-auto px-6 md:px-12 py-24">
          <div className="text-xs font-bold uppercase tracking-[0.3em] text-[#FFD700]">
            {t("referral.eyebrow")}
          </div>
          <h1 className="font-display mt-6 text-5xl sm:text-6xl lg:text-7xl font-bold uppercase tracking-tighter leading-[0.9] text-white">
            {t("referral.title_l1")}
            <br />
            <span className="silver-shimmer">{t("referral.title_l2")}</span>
          </h1>
          <p className="mt-8 max-w-2xl text-base sm:text-lg leading-relaxed text-white/75">
            {t("referral.pitch")}
          </p>

          {/* 3-Step explainer */}
          <div className="mt-16 grid md:grid-cols-3 gap-6">
            {[
              { num: "01", title: t("referral.step1_title"), desc: t("referral.step1_desc"), icon: Share2 },
              { num: "02", title: t("referral.step2_title"), desc: t("referral.step2_desc"), icon: Sparkles },
              { num: "03", title: t("referral.step3_title"), desc: t("referral.step3_desc"), icon: Gift },
            ].map((s) => (
              <div key={s.num} className="border-t border-white/10 pt-6" data-testid={`step-${s.num}`}>
                <div className="flex items-center justify-between">
                  <s.icon size={24} strokeWidth={1.5} className="text-[#FFD700]" />
                  <span className="text-xs font-bold tracking-[0.25em] text-white/30">{s.num}</span>
                </div>
                <h3 className="font-display mt-5 text-xl font-bold uppercase tracking-tight">{s.title}</h3>
                <p className="mt-3 text-sm text-white/65 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── DASHBOARD (only when logged in) ─── */}
      {user && data && (
        <section className="bg-[#000B1A] border-b border-white/10" data-testid="parrainage-dashboard">
          <div className="max-w-5xl mx-auto px-6 md:px-12 py-24">
            <div className="text-xs font-bold uppercase tracking-[0.3em] text-[#C5CDD8]">
              {t("referral.dashboard_eyebrow")}
            </div>
            <h2 className="font-display mt-3 text-3xl sm:text-4xl font-bold uppercase tracking-tighter">
              {t("referral.dashboard_title")}
            </h2>

            {/* Code + share box */}
            <div className="mt-12 border border-[#FFD700]/40 bg-gradient-to-br from-[#FFD700]/10 to-transparent p-8 sm:p-12">
              <div className="text-xs font-bold uppercase tracking-[0.25em] text-[#FFD700]">
                {t("referral.your_code")}
              </div>
              <div
                className="font-display mt-4 text-4xl sm:text-5xl font-bold tracking-widest text-white select-all"
                data-testid="parrainage-code"
              >
                {data.referral_code}
              </div>
              <div className="mt-6 text-sm text-white/70 break-all" data-testid="parrainage-share-url">
                {shareUrl}
              </div>
              <div className="mt-8 flex flex-wrap gap-3">
                <button
                  onClick={copyLink}
                  className="inline-flex items-center gap-2 bg-white text-[#001F3F] hover:bg-[#FFD700] transition-all uppercase tracking-[0.25em] text-xs font-bold px-6 py-3"
                  data-testid="parrainage-copy"
                >
                  <Copy size={14} /> {t("referral.copy")}
                </button>
                <button
                  onClick={shareByEmail}
                  className="inline-flex items-center gap-2 border border-white/30 hover:bg-white/10 transition-all uppercase tracking-[0.25em] text-xs font-bold px-6 py-3"
                  data-testid="parrainage-email"
                >
                  <Mail size={14} /> Email
                </button>
                <button
                  onClick={shareWhatsapp}
                  className="inline-flex items-center gap-2 border border-white/30 hover:bg-white/10 transition-all uppercase tracking-[0.25em] text-xs font-bold px-6 py-3"
                  data-testid="parrainage-whatsapp"
                >
                  <Share2 size={14} /> WhatsApp
                </button>
              </div>
            </div>

            {/* KPI cards */}
            <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4">
              <KpiCard icon={Wallet} label={t("referral.kpi_balance")} value={`${data.credit_balance_eur.toFixed(2)} €`} hint={t("referral.kpi_balance_hint")} testid="kpi-balance" />
              <KpiCard icon={Users} label={t("referral.kpi_total")} value={data.credits_earned_count} hint={t("referral.kpi_total_hint")} testid="kpi-total" />
              <KpiCard icon={Gift} label={t("referral.kpi_yearly")} value={`${data.yearly_used}/${data.yearly_limit}`} hint={t("referral.kpi_yearly_hint")} testid="kpi-yearly" />
              <KpiCard icon={Sparkles} label={t("referral.kpi_per_friend")} value={`${data.parrain_credit_eur.toFixed(0)} €`} hint={t("referral.kpi_per_friend_hint")} testid="kpi-per-friend" />
            </div>

            {/* History */}
            {data.referrals && data.referrals.length > 0 && (
              <div className="mt-12">
                <div className="text-xs font-bold uppercase tracking-[0.25em] text-[#C5CDD8] mb-4">
                  {t("referral.history_title")}
                </div>
                <div className="border border-white/10" data-testid="parrainage-history">
                  {data.referrals.map((r, i) => (
                    <div
                      key={r.id}
                      className={`flex items-center justify-between px-5 py-4 ${i ? "border-t border-white/10" : ""}`}
                    >
                      <div className="text-sm">
                        <div className="font-semibold text-white">{r.filleul_email}</div>
                        <div className="text-xs text-white/50 mt-0.5">{new Date(r.credited_at).toLocaleDateString()}</div>
                      </div>
                      <div className="text-sm font-bold text-[#FFD700]">+ {r.credit_eur.toFixed(2)} €</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* ─── Logged-out CTA ─── */}
      {!authLoading && !user && (
        <section className="py-24 text-center" data-testid="parrainage-cta-section">
          <h3 className="font-display text-2xl sm:text-3xl font-bold uppercase tracking-tighter">
            {t("referral.cta_login_title")}
          </h3>
          <p className="mt-4 text-white/65">{t("referral.cta_login_subtitle")}</p>
          <div className="mt-8 flex justify-center gap-3 flex-wrap">
            <Link
              to="/register"
              className="border border-white bg-white text-[#001F3F] hover:bg-transparent hover:text-white transition-all uppercase tracking-[0.25em] text-xs font-bold px-8 py-4"
              data-testid="parrainage-cta-register"
            >
              {t("referral.cta_register")}
            </Link>
            <Link
              to="/login"
              className="border border-white/30 hover:bg-white/10 transition-all uppercase tracking-[0.25em] text-xs font-bold px-8 py-4"
              data-testid="parrainage-cta-login"
            >
              {t("referral.cta_login")}
            </Link>
          </div>
        </section>
      )}

      {loading && user && (
        <div className="text-center py-24 text-white/60 text-sm">…</div>
      )}
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, hint, testid }) {
  return (
    <div className="border border-white/10 bg-white/5 px-5 py-6" data-testid={testid}>
      <Icon size={20} strokeWidth={1.5} className="text-[#C5CDD8]" />
      <div className="text-xs font-bold uppercase tracking-[0.25em] text-white/60 mt-3">{label}</div>
      <div className="font-display text-3xl font-bold tracking-tight text-white mt-2">{value}</div>
      <div className="text-xs text-white/40 mt-1">{hint}</div>
    </div>
  );
}
